import * as Api from '@ViewModels';
import { yupResolver } from '@hookform/resolvers/yup';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';
import * as AdminViewModels from '../../../../app/viewmodels/AdminViewModels';
import { IRestoreKeyFactsRequest, KeyDateKind } from '../../../extViewmodels';
import { IModalContext, ModalChildComponentContextKey } from '../../../models';
import { useRestoreKeyFactsMutation } from '../../../queries/KeyFacts/useRestoreKeyFacts';
import { Button } from '../../../web/components/Button';
import { CustomDateRange } from '../../../web/components/CustomDateRange';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../web/components/Modal';
import { RadioButton } from '../../../web/components/RadioButton';
import { ISelectBoxOption, SelectBox } from '../../../web/components/SelectBox';
import { SimpleAutoCompleteSearchField } from '../../../web/components/autocomplete/SimpleAutoCompleteSearchField';
import { styleSheet } from './styles';

interface IProps extends IModalContext {
	userSession: AdminViewModels.AdminUserSessionContext;
}

const _RestoreKeyFactsModal = ({ parentModal: { onRequestClose } }: IProps) => {
	const [showMainLoader, setShowMainLoader] = React.useState<boolean>(false);

	const LoaderIndicator = () => {
		return showMainLoader ? <LoadingSpinner type='small' /> : null;
	};

	const handleCancel = () => {
		onRequestClose();
	};

	const accountDetailsContext = React.useContext(AdminViewModels.AccountDetailsContext);

	const changeKeyFactRestoreBy = (restoreKeyFactBy: string) => {
		switch (restoreKeyFactBy) {
			case 'date':
				methods.setValue('contactId', undefined);
				break;
			case 'contact':
				methods.setValue('startDate', undefined);
				methods.setValue('endDate', undefined);
				break;
			default:
				break;
		}
	};
	const keyDateKinds: ISelectBoxOption<string>[] = [
		{ value: KeyDateKind.Birthday, title: 'Birthday' },
		{ value: KeyDateKind.Renewal, title: 'Renewal' },
		{ value: KeyDateKind.Anniversary, title: 'Anniversary' },
		{ value: KeyDateKind.Wedding, title: 'Wedding' },
		{ value: KeyDateKind.FinancialReview, title: 'Financial Review' },
		{ value: KeyDateKind.Custom1, title: 'Custom1' },
		{ value: KeyDateKind.Custom2, title: 'Custom2' },
		{ value: KeyDateKind.Custom3, title: 'Custom3' },
		{ value: KeyDateKind.Custom4, title: 'Custom4' },
		{ value: KeyDateKind.Custom5, title: 'Custom5' },
		{ value: KeyDateKind.Custom6, title: 'Custom6' },
		{ value: KeyDateKind.Custom7, title: 'Custom7' },
		{ value: KeyDateKind.Custom8, title: 'Custom8' },
		{ value: KeyDateKind.Custom9, title: 'Custom9' },
		{ value: KeyDateKind.Custom10, title: 'Custom10' },
	];
	const suppressReasons: ISelectBoxOption<string>[] = [
		{ value: 'Acknowledged', title: 'Acknowledged' },
		{ value: 'Skipped', title: 'Skipped' },
	];

	const impersonationContext = { account: accountDetailsContext.account };
	const postRestoreKeyFactsMutation = useRestoreKeyFactsMutation({
		onSuccess: keyFactsRestored => {
			onRequestClose();
			toast.success(`${keyFactsRestored} KeyFacts restored successfully`);
		},
		impersonationContext,
	});

	const handleContinueClick = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setShowMainLoader(true);
		if (!methods.formState.isSubmitted) {
			const request: IRestoreKeyFactsRequest = {
				keyDateKind: formValues.keyDateKind as KeyDateKind,
				suppressReason: formValues.suppressReason,
			};

			if (formValues.keyFactRestoreBy === 'date') {
				// Only creationStartDate is required
				if (formValues.startDate) {
					request.startDate = formValues.startDate;
					request.endDate = formValues.endDate;
				}
				postRestoreKeyFactsMutation.mutateAsync({ request });
			} else if (formValues.keyFactRestoreBy === 'contact') {
				if (formValues.contactId) {
					postRestoreKeyFactsMutation.mutateAsync({ request, contactId: formValues.contactId });
				} else {
					// Handle case where contact ID is not provided
				}
			}
		}
	};
	const validationSchema = yup.object({
		startDate: yup.date().when('keyFactRestoreBy', (_, schema) => {
			if (formValues.keyFactRestoreBy === 'date') {
				return schema.required('Start date is required');
			}
			return schema.notRequired();
		}),
		endDate: yup.date().notRequired(),
		contactId: yup.string().when('keyFactRestoreBy', (_, schema) => {
			if (formValues.keyFactRestoreBy === 'contact') {
				return schema.required('Contact ID is required');
			}
			return schema.notRequired();
		}),
		keyDateKind: yup.string().required(),
		suppressReason: yup.string().required(),
	});
	const methods = useForm<{
		keyFactRestoreBy: string;
		startDate: Date | undefined;
		endDate: Date | undefined;
		contactId: string;
		keyDateKind: string | undefined;
		suppressReason: string | undefined;
	}>({
		defaultValues: {
			keyFactRestoreBy: 'date',
			startDate: undefined,
			endDate: undefined,
			contactId: '',
			keyDateKind: undefined,
			suppressReason: undefined,
		},
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
	});
	const formValues = methods.watch();

	const onCreateReloadAutoCompleteViewModel = (
		_: Api.ResourceAutoCompleteViewModelType,
		suggestedViewModel: Api.ResourceAutoCompleteViewModel<Api.ResourceAutoCompleteModel>
	) => {
		suggestedViewModel.impersonate({
			account: impersonationContext.account,
		});
		return suggestedViewModel;
	};
	const renderFormFields = () => {
		if (showMainLoader) {
			return <LoaderIndicator />;
		}

		if (formValues.keyFactRestoreBy === 'date') {
			return (
				<div>
					<CustomDateRange
						startDateRequired={false}
						forceSmallText
						defaultOpen={false}
						from={formValues.startDate ? formValues.startDate : undefined}
						to={formValues.endDate ? formValues.endDate : undefined}
						resetDates={() => {
							methods.reset(values => ({
								...values,
								endDate: undefined,
								startDate: undefined,
								endTime: undefined,
								startTime: undefined,
							}));
						}}
						onChange={(from, to) => {
							methods.reset(values => {
								return {
									...values,
									startDate: from ? new Date(moment(from).format('YYYY/MM/DD')) : undefined,
									endDate: to ? new Date(moment(to).format('YYYY/MM/DD')) : undefined,
									startTime: undefined,
									endTime: undefined,
								};
							});
						}}
						showTextInput
					/>
				</div>
			);
		} else if (formValues.keyFactRestoreBy === 'contact') {
			return (
				<Controller
					name='contactId'
					control={methods.control}
					render={({ field: { ref, ...fieldProps } }) => (
						<div className={css(styleSheet.contactIdInputWrapper)}>
							<SimpleAutoCompleteSearchField
								onClear={() => fieldProps.onChange('')}
								onCreateAutoCompleteViewModel={onCreateReloadAutoCompleteViewModel}
								// @ts-ignore
								onItemSelected={(item: ISimpleAutoCompleteSearchFieldItemSelectionEvent<IContact>) => {
									fieldProps.onChange(item.selection.id);
								}}
								resultsLimit={3}
								pageSize={3}
								placeholder='Search contacts from integration'
								// style={styleSheet.reloadAutoComplete}
								type={Api.ResourceAutoCompleteViewModelType.Contact}
								hideResultsFooter={true}
							/>
						</div>
					)}
				/>
			);
		}
	};

	return (
		<FormProvider {...methods}>
			<form onSubmit={handleContinueClick}>
				<div className={css(styleSheet.wrapper)}>
					<h3 className={css(styleSheet.heading)}>Restore Key Facts</h3>
					<p className={css(styleSheet.text)}>Do you want to restore KeyFacts by:</p>
					<div className={css(styleSheet.radioButtonsWrap)}>
						<RadioButton
							checked={formValues.keyFactRestoreBy === 'date'}
							id='Ams360Ownership-Executive'
							name='Ams360Ownership-Executive'
							onChange={() => {
								methods.reset(values => {
									return {
										...values,
										keyFactRestoreBy: 'date',
									};
								});
								changeKeyFactRestoreBy('date');
							}}
						>
							<span>Date</span>
						</RadioButton>
						<RadioButton
							checked={formValues.keyFactRestoreBy === 'contact'}
							id='Ams360Ownership-Representative'
							name='Ams360Ownership-Representative'
							onChange={() => {
								methods.reset(values => {
									return {
										...values,
										keyFactRestoreBy: 'contact',
									};
								});
								changeKeyFactRestoreBy('contact');
							}}
						>
							<span>Contact</span>
						</RadioButton>
					</div>
					{renderFormFields()}
					<Controller
						name='keyDateKind'
						control={methods.control}
						render={({ field: { ref, ...fieldProps } }) => (
							<SelectBox
								placeholder='Select the Kind of Key Date'
								triggerClassName={` ${css(styleSheet.selectTrigger)}`}
								options={keyDateKinds}
								onSelectionChanged={option => fieldProps.onChange(option.value)}
								selectedOption={keyDateKinds.find(x => x.value === formValues.keyDateKind)}
							/>
						)}
					/>
					<Controller
						name='suppressReason'
						control={methods.control}
						render={({ field: { ref, ...fieldProps } }) => (
							<SelectBox
								placeholder='Select the Suppress Reason'
								triggerClassName={` ${css(styleSheet.selectTrigger)}`}
								options={suppressReasons}
								onSelectionChanged={option => fieldProps.onChange(option.value)}
								selectedOption={suppressReasons.find(x => x.value === formValues.suppressReason)}
							/>
						)}
					/>
					<footer className={css(styleSheet.footer)}>
						<Button
							kind='primary'
							className={css(styleSheet.continueButton)}
							label='Continue'
							// onClick={handleContinueClick}
							isLoading={showMainLoader}
							disabled={!methods.formState.isValid && !methods.formState.isSubmitting}
							type='submit'
						/>
						<Button kind='reverse' label='Cancel' onClick={handleCancel} type='button' />
					</footer>
				</div>
			</form>
		</FormProvider>
	);
};

const RestoreKeyFactsModalAsObserver = observer(_RestoreKeyFactsModal);
const RestoreKeyFactsModalWithContext = inject(ModalChildComponentContextKey)(RestoreKeyFactsModalAsObserver);
export const RestoreKeyFactsModal = asModalComponent(RestoreKeyFactsModalWithContext, {
	className: 'schedule-meeting--modal',
	useDefaultHeader: true,
});
